/**
 * Builder mode (non-tech F0). `dev` is the default and leaves the product exactly as it is;
 * `simple` hides developer surfaces (tech stack, model picker, layer/phase wording) and swaps
 * the "copy the prompt" screen for the three doors.
 *
 * The value lives on `user.builder_mode` and rides along the better-auth session
 * (`additionalFields`), so reading it costs no extra request. better-auth caches the session
 * cookie for 5 minutes — after a write we refresh it with `disableCookieCache`.
 */
import { useEffect, useState } from 'react'
import { authClient, useHydratedSession } from '#/lib/auth-client'
import { api } from '#/lib/api'

export type BuilderMode = 'dev' | 'simple'

export function useBuilderMode(): { mode: BuilderMode; simple: boolean; isPending: boolean } {
  const { data: session, isPending } = useHydratedSession()
  // Hydration safety: SSR never has the session cookie's builderMode (it renders the signed-out/
  // loading shape), while the client can already have a cached session at mount — branching on
  // `simple` before the two agree throws a hydration mismatch (seen live in the roadmap's Bystep
  // Agent welcome text). Stay on the SSR-safe 'dev' default for the client's first paint too, and
  // flip right after mount.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  const raw = (session?.user as { builderMode?: string } | undefined)?.builderMode
  const mode: BuilderMode = mounted && raw === 'simple' ? 'simple' : 'dev'
  return { mode, simple: mode === 'simple', isPending: !mounted || isPending }
}

/** Refresh the reactive session atom, including the cookie cache, after a profile write. */
export async function refreshBuilderSession() {
  const session = authClient.$store.atoms.session
  await session.get().refetch({ query: { disableCookieCache: true } })
  const refreshed = session.get()
  if (refreshed.error) throw refreshed.error
  if (!refreshed.data?.user) throw new Error('Session expired')
}

/** PATCH /api/user/builder-mode, then refresh the cached session so the UI flips immediately. */
export async function setBuilderMode(builderMode: BuilderMode) {
  await api<{ builderMode: BuilderMode }>('/api/user/builder-mode', { method: 'PATCH', json: { builderMode } })
  // getSession() fetches the HTTP response but does not update useSession() subscribers.
  await refreshBuilderSession()
  return builderMode
}
